import { ColDef } from 'ag-grid-community';
import React, { FC } from 'react';
import { Project } from 'api/project';
import { Category } from 'api/timesheets/categories';
import {
  CalenderMeta,
  ComputedCalendarEvent,
} from 'api/timesheets/integration/event';
import { Option } from 'components/atoms/select';
import { Translate, useTranslate } from 'hooks/use-translation';
import {
  useCategoryOptions,
  useProjectOptionsForCalendar,
} from '../../../add-adhoc';
import { Grid, GridContainer } from '../component';
import {
  BillableEditor,
  BillableRenderer,
  EventHeaderRenderer,
  NameRenderer,
  OptionRenderer,
  ProjectEditor,
  Selection,
  SelectionEditor,
  CategoryEditor,
  TimeRenderer,
  MandatoryRenderer,
} from './table-components';
import { ActivityRowData, OnChangeParams } from './types';

type OnChange = (row: AtLeast<ActivityRowData, 'eventId'>) => void;

const projectsCol = (
  translate: Translate,
  projectOptions: Option[],
  onChange: OnChange,
  mandateCategory: boolean,
) => ({
  singleClickEdit: true,
  width: 152,
  headerName: translate('project'),
  cellRenderer: 'OptionRenderer',
  field: 'projects',
  cellEditor: 'ProjectEditor',
  headerComponent: 'MandatoryRenderer',
  cellEditorParams: {
    projectOptions,
    onChange: ({
      data,
      api,
      node,
      newValue,
    }: OnChangeParams<Option<Project>>) => {
      onChange({ eventId: data.eventId, projects: newValue });
      if (mandateCategory && !data.category) {
        api?.startEditingCell({ rowIndex: node.rowIndex, colKey: 'category' });
      } else {
        onChange({ eventId: data.eventId, isSelected: true });
        api?.selectNode(node);
        api?.stopEditing();
      }
      window.setTimeout(() => {
        api?.refreshCells({ force: true, rowNodes: [node] }); // when project is changed we need to rerender billable checkbox
      });
    },
  },
});

const categoryCol = (
  translate: Translate,
  categoryOptions: Option[],
  onChange: OnChange,
  mandateCategory: boolean,
): ColDef => ({
  singleClickEdit: true,
  width: 152,
  headerName: translate('timesheets.reports.tabs.category'),
  cellRenderer: 'OptionRenderer',
  field: 'category',
  cellEditor: 'CategoryEditor',
  headerComponent: mandateCategory ? 'MandatoryRenderer' : undefined,
  cellEditorParams: {
    categoryOptions,
    onChange: ({
      data,
      api,
      node,
      newValue,
    }: OnChangeParams<Option<Category>>) => {
      onChange({ eventId: data.eventId, category: newValue });
      if (!data.projects) {
        api?.startEditingCell({
          rowIndex: node.rowIndex,
          colKey: 'projects',
        });
      } else {
        onChange({ eventId: data.eventId, isSelected: true });
        api?.selectNode(node);
        api?.stopEditing();
      }
      window.setTimeout(() => {
        api?.refreshCells({ force: true, rowNodes: [node] }); // when project is changed we need to rerender billable checkbox
      });
    },
  },
});

const eventName = (calenderMeta: CalenderMeta) => ({
  width: 246,
  field: 'eventName',
  cellRenderer: NameRenderer,
  editable: false,
  headerComponent: 'EventHeaderRenderer',
  headerComponentParams: {
    calenderMeta,
  },
});

const duration = (translate: Translate) => ({
  width: 88,
  headerName: translate('time'),
  cellRenderer: TimeRenderer,
  field: 'duration',
  editable: false,
});

const billing = (translate: Translate, onChange: OnChange) => ({
  singleClickEdit: true,
  width: 80,
  headerName: translate('timesheets.column.billable'),
  cellRenderer: 'BillableRenderer',
  cellEditor: 'BillableEditor',
  field: 'billable',
  cellEditorParams: {
    onChange: ({ data, newValue: billable }: OnChangeParams<boolean>) => {
      onChange({ eventId: data.eventId, billable });
    },
  },
});

export const ActivityTable: FC<{
  rowData: ActivityRowData[];
  calenderMeta: CalenderMeta;
  onChange: OnChange;
  onGridReady: (params: any) => void;
  projects?: Project[];
  mandateCategory: boolean;
  timeEntryCategories: Category[];
}> = ({
  rowData,
  calenderMeta,
  onChange,
  onGridReady,
  projects,
  mandateCategory,
  timeEntryCategories,
}) => {
  const translate = useTranslate();
  const projectOptions = useProjectOptionsForCalendar(projects);
  const categoryOptions = useCategoryOptions(timeEntryCategories);

  return (
    <GridContainer className="ag-theme-balham">
      <Grid
        rowData={rowData}
        size="large"
        immutableData
        domLayout="autoHeight"
        defaultColDef={{
          filter: false,
          sortable: false,
          resizable: false,
          wrapText: true,
          suppressMovable: true,
          suppressMenu: true,
          cellStyle: (params) => ({
            borderColor: 'transparent !important',
            opacity: params.data.marked ? '0.4' : '1',
          }),
          editable: (params) => !params.data.marked,
          rowDrag: false,
        }}
        suppressContextMenu
        autoGroupColumnDef={{
          headerName: '',
          pinned: 'left',
          lockPinned: true,
          lockPosition: true,
          initialWidth: 40,
          cellRenderer: 'Selection',
          cellEditor: 'SelectionEditor',
          field: 'isSelected',
          singleClickEdit: true,
          cellStyle: { borderColor: 'transparent !important' },
          cellEditorParams: {
            onChange: ({
              api,
              node,
              newValue: isSelected,
              data,
            }: OnChangeParams<boolean>) => {
              onChange({ eventId: data.eventId, isSelected });
              if (isSelected) {
                api?.selectNode(node);
              } else {
                api?.deselectNode(node);
              }
            },
            mandateCategory,
          },
        }}
        suppressRowClickSelection
        treeData
        animateRows
        gridOptions={{
          enterMovesDownAfterEdit: true,
          enterMovesDown: false,
          immutableData: true,
        }}
        frameworkComponents={{
          ProjectEditor,
          BillableEditor,
          SelectionEditor,
          EventHeaderRenderer,
          MandatoryRenderer,
          Selection,
          OptionRenderer,
          BillableRenderer,
          CategoryEditor,
        }}
        groupDefaultExpanded={-1}
        getRowNodeId={(d: ComputedCalendarEvent) => d.eventId}
        onGridReady={onGridReady}
        headerHeight={32}
        rowSelection="multiple"
        getDataPath={(data) => [data.eventId]}
        isRowSelectable={() => true}
        stopEditingWhenGridLosesFocus
        tooltipShowDelay={0}
        columnDefs={[
          eventName(calenderMeta),
          projectsCol(translate, projectOptions, onChange, mandateCategory),
          categoryCol(translate, categoryOptions, onChange, mandateCategory),
          duration(translate),
          billing(translate, onChange),
        ]}
      />
    </GridContainer>
  );
};
